import { useState } from 'react';
import { jarvisAPI } from '../api/client';
import { Sparkles, Loader2, X, ChevronLeft, Check } from 'lucide-react';
import MicButton from './MicButton';

interface JarvisFieldAssistProps {
  field: string;
  title: string;
  intro: string;
  questions: string[];
  onApply: (draft: string) => void;
  onClose: () => void;
}

// Optional, snapshot-style "Help from Jarvis": instead of auto-writing canned
// text, Jarvis asks a few guiding questions and then assembles the member's OWN
// answers into a paragraph they can edit or discard. For members who would
// rather just type, they never open this — the plain textarea is always there.
export default function JarvisFieldAssist({
  field,
  title,
  intro,
  questions,
  onApply,
  onClose,
}: JarvisFieldAssistProps) {
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ''));
  const [phase, setPhase] = useState<'interview' | 'draft'>('interview');
  const [draft, setDraft] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const hasAnyAnswer = answers.some((a) => a.trim().length > 0);

  const setAnswer = (i: number, value: string) => {
    setAnswers((prev) => prev.map((a, idx) => (idx === i ? value : a)));
  };

  const generate = async () => {
    setIsGenerating(true);
    try {
      const payload = questions
        .map((question, i) => ({ question, answer: answers[i] }))
        .filter((qa) => qa.answer.trim().length > 0);
      const result = await jarvisAPI.profileInterview(field, payload);
      setDraft(result.draft || payload.map((qa) => qa.answer.trim()).join(' '));
      setPhase('draft');
    } catch {
      // Fall back to stitching their own answers so nothing is lost.
      setDraft(answers.filter((a) => a.trim()).join(' '));
      setPhase('draft');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-base font-semibold text-navy flex items-center gap-2">
            <Sparkles size={18} className="text-gold" />
            Help from Ask IF · {title}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {phase === 'interview' && (
            <>
              <p className="text-sm text-gray-600">{intro}</p>
              {questions.map((q, i) => (
                <div key={i}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{q}</label>
                  <div className="relative">
                    <textarea
                      value={answers[i]}
                      onChange={(e) => setAnswer(i, e.target.value)}
                      rows={2}
                      placeholder="In your own words..."
                      className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                    <div className="absolute right-2 top-2">
                      <MicButton value={answers[i]} onChange={(next) => setAnswer(i, next)} />
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400">
                Answer what you'd like — Ask IF only uses what you write and won't invent anything.
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={!hasAnyAnswer || isGenerating}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
              >
                {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                Draft this from my answers
              </button>
            </>
          )}

          {phase === 'draft' && (
            <>
              <p className="text-sm text-gray-600">
                Here's a draft built from your answers. Edit it however you like before adding it.
              </p>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
              />
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setPhase('interview')}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-navy hover:bg-gray-50 rounded-lg"
                >
                  <ChevronLeft size={16} />
                  Back to questions
                </button>
                <button
                  type="button"
                  onClick={() => onApply(draft)}
                  disabled={!draft.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gold text-white rounded-lg text-sm font-medium hover:bg-gold-dark transition disabled:opacity-50"
                >
                  <Check size={16} />
                  Use this
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
